import React, { useEffect, useMemo, useState } from 'react';
import { SyutSelect } from 'components/ui/SyutSelect';
import type { SelectOption } from 'components/ui/SyutSelect';
import { orgService, type OrgBusinessUnitLookup } from 'api/services/orgService';

/*
 * The organisation unit picker (Kitchens and org units, 2026-10-22). One component for every
 * screen that links a record to an org unit: DineOps outlets, kitchens, and requisitions.
 * Units are created only in Organisation -> Business Units; this picks an existing one.
 *
 * Lists the active units of the entity (all entities when none is given), narrowed to the
 * types asked; the property's own unit and the units under it come first. Each option shows
 * the unit's path, so "Food & Beverage" of two hotels can be told apart. The server checks the
 * choice again on save (active, same entity).
 */

export interface BusinessUnitSelectProps {
  value: string;
  onChange: (businessUnitId: string) => void;
  /** Only this entity's units. */
  entityId?: string | null;
  /** List this property's units first. */
  propertyId?: string | null;
  /** Unit type codes, e.g. ['DEPARTMENT', 'COST_CENTER']. */
  types?: string[];
  label?: string;
  placeholder?: string;
  required?: boolean;
  isDisabled?: boolean;
  error?: string;
  helperText?: string;
  /** The saved unit, so its name shows even when it is no longer offered (made inactive, other type). */
  seed?: { id?: string | null; name?: string | null };
}

const optionLabel = (u: OrgBusinessUnitLookup) =>
  `${u.path}${u.unitTypeName ? ` · ${u.unitTypeName}` : ''} (${u.unitCode})`;

export const BusinessUnitSelect: React.FC<BusinessUnitSelectProps> = ({
  value, onChange, entityId, propertyId, types, label = 'Business Unit', placeholder,
  required, isDisabled, error, helperText, seed,
}) => {
  const [units, setUnits] = useState<OrgBusinessUnitLookup[]>([]);
  const [loading, setLoading] = useState(false);
  const typeKey = (types ?? []).join(',');

  useEffect(() => {
    let live = true;
    setLoading(true);
    orgService.getBusinessUnits(entityId || undefined, { types: typeKey ? typeKey.split(',') : undefined, propertyId: propertyId || undefined })
      .then((res) => { if (live) setUnits(res.data ?? []); })
      .catch(() => { if (live) setUnits([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [entityId, propertyId, typeKey]);

  const options: SelectOption[] = useMemo(() => units.map((u) => ({ value: u.id, label: optionLabel(u) })), [units]);
  const seedOptions = seed?.id && seed.name && !units.some((u) => u.id === seed.id)
    ? [{ value: seed.id, label: seed.name }]
    : undefined;
  const empty = !loading && units.length === 0;

  return (
    <SyutSelect
      label={label}
      options={seedOptions ? [...options, ...seedOptions] : options}
      value={value}
      onChange={(v) => onChange((v as string) || '')}
      placeholder={placeholder ?? (empty ? 'No business units yet' : 'Select business unit...')}
      isClearable={!required}
      isSearchable
      isLoading={loading}
      isDisabled={isDisabled}
      required={required}
      error={error}
      helperText={helperText ?? (empty ? 'No business units yet — create them in Organisation → Business Units.' : undefined)}
      noOptionsMessage="No matching business unit"
      size="md"
      menuPortalTarget={document.body}
    />
  );
};

export default BusinessUnitSelect;
